/**
 * Service worker registration with auto-update.
 * - Registers `/service-worker.js` after the page loads (prod or dev).
 * - When a new SW is found, signals it to take over immediately and reloads
 *   the page ONCE so users always see the latest UI without a manual refresh.
 * - Hard-guarded: the auto-reload fires at most once per browser session, so a
 *   broken/looping SW can never pin a tab in a refresh-cycle (the previous
 *   implementation could; this version cannot).
 *
 * Call once from `index.js`.
 */
const RELOADED_KEY = "hpl_sw_reloaded_v1";

export function registerServiceWorker() {
    if (!("serviceWorker" in navigator)) return;

    // Avoid running inside iframes (e.g. embed previews)
    if (window.self !== window.top) return;

    const swUrl = `${process.env.PUBLIC_URL || ""}/service-worker.js`;

    window.addEventListener("load", () => {
        navigator.serviceWorker
            .register(swUrl)
            .then((reg) => {
                // Trigger an update check whenever the tab becomes visible again
                document.addEventListener("visibilitychange", () => {
                    if (document.visibilityState === "visible") reg.update().catch(() => {});
                });

                reg.addEventListener("updatefound", () => {
                    const installing = reg.installing;
                    if (!installing) return;
                    installing.addEventListener("statechange", () => {
                        if (installing.state === "installed" && navigator.serviceWorker.controller) {
                            // New version installed → activate it now. We will reload
                            // ONCE in response to the resulting controllerchange event.
                            installing.postMessage("SKIP_WAITING");
                        }
                    });
                });
            })
            .catch((err) => {
                // Don't crash the app over SW failures (rare; usually CSP/HTTPS issues in dev)
                // eslint-disable-next-line no-console
                console.warn("SW registration failed:", err);
            });

        // controllerchange fires when a freshly-activated SW takes over. We reload
        // once so the page picks up the new bundle hashes, but never twice in the
        // same session — that's the refresh-loop guard.
        navigator.serviceWorker.addEventListener("controllerchange", () => {
            try {
                if (sessionStorage.getItem(RELOADED_KEY) === "1") return;
                sessionStorage.setItem(RELOADED_KEY, "1");
            } catch { /* private mode — accept best-effort behaviour */ }
            window.location.reload();
        });
    });
}
